import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { games, categories } from "@/mocks/games";

const categoryName: Record<string, string> = {
  campus: "校园纯爱", fantasy: "奇幻冒险", city: "都市职场", thriller: "悬疑惊悚",
  harem: "后宫多线", healing: "治愈日常", dark: "暗黑题材", world: "架空世界观",
};

function Breadcrumb() {
  return (
    <nav aria-label="面包屑" className="mx-auto w-full max-w-7xl px-4 pt-6 md:px-6">
      <ol className="flex items-center gap-1.5 text-xs text-foreground-500">
        <li><Link to="/" className="hover:text-accent-600">首页</Link></li>
        <li><i className="ri-arrow-right-s-line text-foreground-300"></i></li>
        <li aria-current="page" className="font-medium text-foreground-800">排行榜</li>
      </ol>
    </nav>
  );
}

export default function Ranking() {
  const [params, setParams] = useSearchParams();
  const [activeCat, setActiveCat] = useState<string>(params.get("cat") || "all");
  const [keyword, setKeyword] = useState<string>(params.get("q") || "");
  const [searchInput, setSearchInput] = useState<string>(params.get("q") || "");

  useEffect(() => {
    document.title = "工口游戏排行榜 | 幻夜志";
  }, []);

  useEffect(() => {
    setActiveCat(params.get("cat") || "all");
    setKeyword(params.get("q") || "");
    setSearchInput(params.get("q") || "");
  }, [params]);

  const list = useMemo(() => {
    let arr = [...games].sort((a, b) => b.score - a.score);
    if (activeCat !== "all") arr = arr.filter((g) => g.category === activeCat);
    if (keyword) {
      const k = keyword.toLowerCase();
      arr = arr.filter(
        (g) =>
          g.title.toLowerCase().includes(k) ||
          g.sub.toLowerCase().includes(k) ||
          g.tags.some((t) => t.toLowerCase().includes(k)) ||
          categoryName[g.category].includes(keyword)
      );
    }
    return arr;
  }, [activeCat, keyword]);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setKeyword(searchInput.trim());
  };

  return (
    <>
      <Breadcrumb />
      <section className="mx-auto w-full max-w-7xl px-4 pb-16 pt-6 md:px-6 md:pb-20">
        <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-accent-600">Ranking</p>
            <h1 className="mt-2 font-display text-2xl font-bold text-foreground-950 md:text-3xl">
              工口游戏排行榜
            </h1>
            <p className="mt-2 max-w-xl text-sm text-foreground-600">
              按口碑综合分排序，覆盖八大类型。你可以切换类型标签，或直接搜索作品名称与关键词。
            </p>
          </div>
          <form onSubmit={submitSearch} className="flex w-full max-w-sm gap-2 sm:items-center sm:rounded-full sm:bg-background-100 sm:p-1.5">
            <div className="relative flex-1">
              <i className="ri-search-line pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400"></i>
              <input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="搜索作品或关键词"
                className="w-full rounded-full bg-background-100 py-3 pl-11 pr-4 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 sm:bg-transparent sm:py-2.5"
              />
            </div>
            <button type="submit" className="rounded-full bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 whitespace-nowrap sm:py-2.5">
              搜索
            </button>
          </form>
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          <button
            onClick={() => setActiveCat("all")}
            className={`rounded-full px-4 py-2 text-sm transition-colors whitespace-nowrap ${activeCat === "all" ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-800"}`}
          >
            综合口碑榜
          </button>
          {categories.map((c) => (
            <button
              key={c.id}
              onClick={() => setActiveCat(c.id)}
              className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm transition-colors whitespace-nowrap ${activeCat === c.id ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-800"}`}
            >
              <i className={c.icon}></i>
              {c.name}
            </button>
          ))}
        </div>

        {keyword && (
          <p className="mt-5 text-sm text-foreground-600">
            搜索「<span className="font-medium text-accent-700">{keyword}</span>」共找到{" "}
            <span className="font-bold text-foreground-950">{list.length}</span> 部作品
            <button onClick={() => { setSearchInput(""); setKeyword(""); }} className="ml-2 text-accent-600 underline-offset-2 hover:underline">
              清除搜索
            </button>
          </p>
        )}

        <div className="mt-6 overflow-hidden rounded-2xl border border-background-200/70 bg-background-100">
          {list.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-3 py-20 text-center">
              <i className="ri-emotion-sad-line text-4xl text-foreground-300"></i>
              <p className="text-sm text-foreground-600">没有找到匹配的作品，换个关键词试试吧。</p>
            </div>
          ) : (
            <ol className="divide-y divide-background-200/70">
              {list.map((g, i) => (
                <li key={g.id}>
                  <Link
                    to={`/review/${g.id}`}
                    className="group flex items-center gap-4 bg-background-50 px-4 py-4 transition-colors hover:bg-primary-50/60 md:px-6"
                  >
                    <span
                      className={`font-display flex w-9 shrink-0 items-center justify-center text-2xl font-black ${
                        i === 0 ? "text-primary-500" : i === 1 ? "text-accent-500" : i === 2 ? "text-secondary-500" : "text-foreground-300"
                      }`}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <img src={g.cover} alt={`${g.title} 封面`} className="h-16 w-12 shrink-0 rounded-md object-cover object-top" />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="truncate font-display text-base font-bold text-foreground-950 group-hover:text-accent-700">
                          {g.title}
                        </h3>
                        {g.isMasterpiece && (
                          <span className="rounded-full bg-accent-500 px-2 py-0.5 text-[10px] font-medium text-background-50">神作</span>
                        )}
                        {g.isBeginner && (
                          <span className="rounded-full bg-primary-100 px-2 py-0.5 text-[10px] font-medium text-primary-700">入门</span>
                        )}
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-foreground-500">
                        <span>{categoryName[g.category]}</span>
                        <span className="text-foreground-300">·</span>
                        <span>{g.studio}</span>
                        <span className="text-foreground-300">·</span>
                        <span>{g.year}</span>
                      </div>
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {g.tags.slice(0, 3).map((t) => (
                          <span key={t} className="rounded-full bg-secondary-100 px-2 py-0.5 text-[10px] text-secondary-700">{t}</span>
                        ))}
                      </div>
                    </div>
                    <div className="flex shrink-0 flex-col items-end">
                      <div className="font-display text-xl font-black text-primary-600">{g.score.toFixed(1)}</div>
                      <div className="text-[10px] text-foreground-400">口碑分</div>
                    </div>
                  </Link>
                </li>
              ))}
            </ol>
          )}
        </div>
      </section>
    </>
  );
}